/**
 * Priya AI — storefront shopping-assistant chat widget
 * Priyadarshini Handloom (priyaodisha.com)
 *
 * A self-contained <priya-chat> web component (Shadow DOM, no dependencies).
 * Streams from the shopping-assistant agent over POST + Server-Sent-Events:
 *
 *   POST {backend}/agents/shopping-assistant/{sessionId}   body: {"text": "..."}
 *   \u2192 text/event-stream  (frames separated by \n\n)
 *       event: text      data: {"delta":"..."}          streamed assistant prose
 *       event: product   data: {title,handle,url?,image?,price,compare_at_price?,in_stock}
 *       event: checkout  data: {url, skipped?}
 *       event: done      data: {}
 *       event: error     data: {message}
 *
 * EventSource is GET-only, so we use fetch() + ReadableStream + a manual SSE
 * frame parser (see _stream / _frame).
 *
 * Product cards render in a horizontal scroll-snap carousel.
 *
 * price and compare_at_price arrive as correct, pre-formatted \u20b9 strings —
 * rendered as received. compare_at_price is present only on genuine markdowns.
 */
(function () {
  if (window.customElements && customElements.get('priya-chat')) return;

  var DEFAULT_BACKEND = 'https://priya-shoping-assistant.ongrid.workers.dev';
  var DEFAULT_SHOP = 'https://priyaodisha.com';
  var SESSION_KEY = 'priya-chat-session';
  var MAX_LEN = 4000;

  // Use real '#' here — encodeURIComponent escapes it once. (Pre-escaping to
  // %23 would double-encode and the SVG would fall back to a black fill.)
  var PLACEHOLDER =
    'data:image/svg+xml,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160">' +
        '<rect width="160" height="160" fill="#f4ece8"/>' +
        '<path d="M40 104l30-34 18 20 14-16 22 30z" fill="#dcc7bd"/>' +
        '<circle cx="58" cy="54" r="11" fill="#dcc7bd"/></svg>'
    );

  var SVG = {
    chat: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
    close: '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    send: '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>',
    spark: '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 0l2.6 8.4L23 11l-8.4 2.6L12 22l-2.6-8.4L1 11l8.4-2.6z"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>'
  };

  var CSS = [
    ':host{',
    '--pc-ink:#2a1416;--pc-magenta:#990047;--pc-magenta-d:#73003a;--pc-gold:#e08a1e;--pc-gold-l:#f6a13a;',
    '--pc-cream:#faf4ef;--pc-card:#fff;--pc-muted:#8a7a74;--pc-line:rgba(110,30,55,.12);',
    "--pc-body:var(--typeBasePrimary,'Lato'),var(--typeBaseFallback,system-ui,sans-serif);",
    "--pc-display:var(--typeHeaderPrimary,'Domine'),var(--typeHeaderFallback,Georgia,serif);",
    'all:initial;font-family:var(--pc-body);color:var(--pc-ink);-webkit-font-smoothing:antialiased;}',
    '*,*::before,*::after{box-sizing:border-box;}',
    'button{font-family:inherit;}',

    /* ---- Launcher ---- */
    '.pc-launch{position:fixed;right:22px;bottom:22px;z-index:2147483000;display:inline-flex;align-items:center;gap:10px;',
    'padding:13px 19px 13px 16px;border:0;cursor:pointer;border-radius:999px;color:#fff;font-size:15px;font-weight:700;',
    'letter-spacing:.2px;background:linear-gradient(135deg,var(--pc-magenta),#bd0058 55%,var(--pc-magenta-d));',
    'box-shadow:0 14px 34px -10px rgba(115,0,58,.6),inset 0 1px 0 rgba(255,255,255,.18);',
    'transition:transform .2s cubic-bezier(.34,1.56,.64,1),box-shadow .2s,opacity .2s;overflow:hidden;}',
    '.pc-launch::after{content:"";position:absolute;inset:0;background:linear-gradient(115deg,transparent 35%,rgba(255,255,255,.28) 50%,transparent 65%);',
    'transform:translateX(-130%);animation:pc-sheen 5.5s ease-in-out 1.2s infinite;}',
    '@keyframes pc-sheen{0%,72%{transform:translateX(-130%);}86%,100%{transform:translateX(130%);}}',
    '.pc-launch:hover{transform:translateY(-3px);box-shadow:0 20px 44px -12px rgba(115,0,58,.66);}',
    '.pc-launch .pc-sparkle{color:var(--pc-gold-l);display:inline-flex;}',
    '.pc-launch.is-open{transform:scale(.5) translateY(12px);opacity:0;pointer-events:none;}',
    '@media(max-width:520px){.pc-launch{right:16px;bottom:16px;padding:14px;} .pc-launch .pc-launch-label{display:none;}}',
    '@media(prefers-reduced-motion:reduce){.pc-launch::after{animation:none;display:none;}}',

    /* ---- Panel ---- */
    '.pc-panel{position:fixed;right:22px;bottom:22px;z-index:2147483000;width:400px;max-width:calc(100vw - 28px);',
    'height:648px;max-height:calc(100vh - 40px);display:flex;flex-direction:column;background:var(--pc-cream);',
    'border-radius:24px;overflow:hidden;border:1px solid var(--pc-line);',
    'box-shadow:0 30px 80px -24px rgba(50,8,28,.5),0 2px 8px rgba(50,8,28,.08);',
    'opacity:0;transform:translateY(18px) scale(.97);transform-origin:bottom right;pointer-events:none;',
    'transition:opacity .26s ease,transform .3s cubic-bezier(.34,1.4,.5,1);}',
    '.pc-panel.is-open{opacity:1;transform:none;pointer-events:auto;}',
    '@media(max-width:520px){.pc-panel{right:0;bottom:0;width:100vw;max-width:100vw;height:100dvh;max-height:100dvh;border-radius:0;}}',

    /* ---- Header ---- */
    '.pc-head{position:relative;display:flex;align-items:center;gap:12px;padding:16px 16px 17px;color:#fff;',
    'background:linear-gradient(135deg,var(--pc-magenta-d),var(--pc-magenta) 60%,#b4005a);}',
    '.pc-head::after{content:"";position:absolute;left:0;right:0;bottom:0;height:2px;',
    'background:linear-gradient(90deg,transparent,var(--pc-gold-l),var(--pc-gold),var(--pc-gold-l),transparent);}',
    '.pc-mark{width:38px;height:38px;border-radius:50%;flex:0 0 auto;display:grid;place-items:center;',
    'background:radial-gradient(circle at 35% 30%,rgba(255,255,255,.28),rgba(255,255,255,.08));',
    'box-shadow:inset 0 0 0 1px rgba(255,255,255,.18);}',
    '.pc-mark svg{color:var(--pc-gold-l);}',
    '.pc-titles{flex:1;min-width:0;}',
    '.pc-titles h2{margin:0;font-family:var(--pc-display);font-size:17px;font-weight:700;line-height:1.05;letter-spacing:.2px;}',
    '.pc-titles p{margin:2px 0 0;font-size:11.5px;opacity:.82;display:flex;align-items:center;gap:6px;}',
    '.pc-dot{width:6px;height:6px;border-radius:50%;background:#46d39a;box-shadow:0 0 0 0 rgba(70,211,154,.6);animation:pc-pulse 2.4s infinite;}',
    '@keyframes pc-pulse{0%{box-shadow:0 0 0 0 rgba(70,211,154,.6);}70%{box-shadow:0 0 0 6px rgba(70,211,154,0);}100%{box-shadow:0 0 0 0 rgba(70,211,154,0);}}',
    '.pc-x{margin-left:auto;flex:0 0 auto;background:rgba(255,255,255,.13);border:0;color:#fff;cursor:pointer;width:33px;height:33px;',
    'border-radius:50%;display:grid;place-items:center;transition:background .15s,transform .15s;}',
    '.pc-x:hover{background:rgba(255,255,255,.26);transform:rotate(90deg);}',

    /* ---- Log ---- */
    '.pc-log{flex:1;overflow-y:auto;overflow-x:hidden;padding:18px 14px 10px;display:flex;flex-direction:column;gap:15px;',
    'background:radial-gradient(120% 60% at 100% 0%,rgba(224,138,30,.06),transparent 60%),var(--pc-cream);}',
    '.pc-log::-webkit-scrollbar{width:7px;}',
    '.pc-log::-webkit-scrollbar-thumb{background:rgba(115,0,58,.16);border-radius:8px;}',
    '.pc-msg{max-width:90%;display:flex;flex-direction:column;gap:9px;animation:pc-rise .35s ease both;}',
    '@keyframes pc-rise{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}',
    '.pc-msg.user{align-self:flex-end;align-items:flex-end;max-width:84%;}',
    '.pc-msg.bot{align-self:flex-start;align-items:stretch;}',
    '.pc-bubble{padding:11px 14px;border-radius:16px;font-size:14px;line-height:1.55;word-wrap:break-word;overflow-wrap:anywhere;align-self:flex-start;}',
    '.pc-msg.user .pc-bubble{background:linear-gradient(135deg,var(--pc-magenta),#b30058);color:#fff;border-bottom-right-radius:5px;',
    'box-shadow:0 6px 16px -8px rgba(115,0,58,.5);align-self:flex-end;}',
    '.pc-msg.bot .pc-bubble{background:var(--pc-card);color:var(--pc-ink);border:1px solid var(--pc-line);border-bottom-left-radius:5px;',
    'box-shadow:0 4px 14px -10px rgba(50,8,28,.3);}',
    '.pc-bubble:empty{display:none;}',
    '.pc-bubble a{color:var(--pc-magenta);text-decoration:underline;text-underline-offset:2px;overflow-wrap:anywhere;}',
    '.pc-bubble strong{font-weight:700;color:var(--pc-magenta-d);}',
    '.pc-bubble ul,.pc-bubble ol{margin:6px 0;padding-left:20px;}',
    '.pc-bubble .pc-h{font-family:var(--pc-display);font-weight:700;font-size:14px;margin:5px 0 2px;color:var(--pc-magenta-d);}',
    '.pc-tablewrap{overflow-x:auto;margin:7px 0;border:1px solid var(--pc-line);border-radius:11px;-webkit-overflow-scrolling:touch;}',
    '.pc-table{border-collapse:collapse;font-size:12.5px;width:100%;}',
    '.pc-table th,.pc-table td{padding:7px 11px;text-align:left;border-bottom:1px solid var(--pc-line);white-space:nowrap;}',
    '.pc-table th{background:rgba(153,0,71,.06);font-weight:700;color:var(--pc-magenta-d);}',
    '.pc-table tbody tr:last-child td{border-bottom:0;}',
    '.pc-table td .pc-price,.pc-table strong{color:var(--pc-magenta);}',

    /* ---- Typing ---- */
    '.pc-typing{align-self:flex-start;display:inline-flex;gap:5px;padding:13px 15px;background:var(--pc-card);',
    'border:1px solid var(--pc-line);border-radius:16px;border-bottom-left-radius:5px;}',
    '.pc-typing span{width:7px;height:7px;border-radius:50%;background:var(--pc-magenta);opacity:.45;animation:pc-bounce 1.1s infinite;}',
    '.pc-typing span:nth-child(2){animation-delay:.16s;}.pc-typing span:nth-child(3){animation-delay:.32s;}',
    '@keyframes pc-bounce{0%,60%,100%{transform:translateY(0);opacity:.4;}30%{transform:translateY(-5px);opacity:1;}}',
    '.pc-notice{align-self:flex-start;font-size:12px;color:var(--pc-muted);font-style:italic;padding:2px 4px;}',
    '.pc-notice:empty{display:none;}',

    /* ---- Carousel ---- */
    '.pc-carousel{position:relative;margin:1px 0;}',
    '.pc-carousel:empty{display:none;}',
    '.pc-track{display:flex;gap:11px;overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;',
    'padding:3px 2px 9px;scrollbar-width:none;-ms-overflow-style:none;scroll-padding-left:2px;}',
    '.pc-track::-webkit-scrollbar{display:none;}',
    '.pc-fade{position:absolute;top:0;bottom:9px;width:30px;pointer-events:none;opacity:0;transition:opacity .2s;z-index:2;}',
    '.pc-fade.l{left:0;background:linear-gradient(90deg,var(--pc-cream),transparent);}',
    '.pc-fade.r{right:0;background:linear-gradient(270deg,var(--pc-cream),transparent);}',
    '.pc-carousel[data-l="1"] .pc-fade.l{opacity:1;}.pc-carousel[data-r="1"] .pc-fade.r{opacity:1;}',
    '.pc-nav{position:absolute;top:calc(50% - 16px);transform:translateY(-50%);width:32px;height:32px;border-radius:50%;',
    'border:1px solid var(--pc-line);background:rgba(255,255,255,.94);color:var(--pc-magenta);cursor:pointer;z-index:3;',
    'display:grid;place-items:center;box-shadow:0 4px 12px -4px rgba(50,8,28,.32);transition:opacity .2s,transform .15s,background .15s;opacity:0;}',
    '.pc-nav:hover{background:var(--pc-magenta);color:#fff;}',
    '.pc-nav.prev{left:5px;}.pc-nav.prev svg{transform:rotate(180deg);}.pc-nav.next{right:5px;}',
    '.pc-carousel[data-l="1"] .pc-nav.prev{opacity:1;}.pc-carousel[data-r="1"] .pc-nav.next{opacity:1;}',

    /* ---- Card ---- */
    '.pc-card{flex:0 0 158px;width:158px;scroll-snap-align:start;display:flex;flex-direction:column;background:var(--pc-card);',
    'border:1px solid var(--pc-line);border-radius:15px;overflow:hidden;text-decoration:none;color:inherit;',
    'box-shadow:0 5px 16px -12px rgba(50,8,28,.4);transition:transform .18s ease,box-shadow .18s ease;animation:pc-rise .4s ease both;}',
    '.pc-card:hover{transform:translateY(-3px);box-shadow:0 14px 26px -12px rgba(50,8,28,.42);}',
    '.pc-imgwrap{position:relative;aspect-ratio:1/1;background:var(--pc-cream);overflow:hidden;}',
    '.pc-imgwrap img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .4s ease;}',
    '.pc-card:hover .pc-imgwrap img{transform:scale(1.06);}',
    '.pc-card.oos .pc-imgwrap img{filter:grayscale(.55) opacity(.72);}',
    '.pc-tag{position:absolute;left:8px;top:8px;font-size:10px;font-weight:800;letter-spacing:.4px;padding:3px 8px;border-radius:7px;text-transform:uppercase;}',
    '.pc-tag.oos{background:rgba(42,20,22,.82);color:#fff;}',
    '.pc-tag.sale{background:linear-gradient(135deg,var(--pc-gold),var(--pc-gold-l));color:#3a2400;}',
    '.pc-cbody{padding:9px 10px 11px;display:flex;flex-direction:column;gap:5px;flex:1;}',
    '.pc-ctitle{font-size:12px;line-height:1.32;font-weight:600;color:var(--pc-ink);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:2.2em;}',
    '.pc-prices{margin-top:auto;display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;}',
    '.pc-price{font-size:13.5px;font-weight:800;color:var(--pc-magenta);}',
    '.pc-was{font-size:11px;color:var(--pc-muted);text-decoration:line-through;}',
    '.pc-card-link{display:flex;flex-direction:column;flex:1;text-decoration:none;color:inherit;}',
    '.pc-add{margin:0 8px 9px;padding:7px 8px;border:1px solid var(--pc-magenta);background:#fff;color:var(--pc-magenta);',
    'font-size:11.5px;font-weight:700;border-radius:9px;cursor:pointer;transition:background .14s,color .14s;}',
    '.pc-add:hover{background:var(--pc-magenta);color:#fff;}',

    /* Sections (grouped product carousels) */
    '.pc-sections{display:flex;flex-direction:column;gap:13px;}',
    '.pc-sections:empty{display:none;}',
    '.pc-section-head{font-family:var(--pc-display);font-weight:700;font-size:13px;color:var(--pc-magenta-d);margin:1px 2px 2px;}',

    /* Cart panel (event:cart) */
    '.pc-cartwrap:empty{display:none;}',
    '.pc-cart{background:#fff;border:1px solid var(--pc-line);border-radius:15px;overflow:hidden;box-shadow:0 5px 16px -12px rgba(50,8,28,.4);}',
    '.pc-cart--empty{padding:18px;text-align:center;color:var(--pc-muted);font-size:13px;}',
    '.pc-chead{display:flex;align-items:baseline;justify-content:space-between;padding:11px 13px;border-bottom:1px solid var(--pc-line);background:rgba(153,0,71,.04);}',
    '.pc-ctitle2{font-family:var(--pc-display);font-weight:700;font-size:14px;color:var(--pc-magenta-d);}',
    '.pc-ccount{font-size:11.5px;color:var(--pc-muted);font-weight:600;}',
    '.pc-clines{max-height:232px;overflow-y:auto;}',
    '.pc-cline{display:flex;align-items:center;gap:10px;padding:9px 13px;border-bottom:1px solid var(--pc-line);}',
    '.pc-cline:last-child{border-bottom:0;}',
    '.pc-cthumb{flex:0 0 auto;width:46px;height:46px;border-radius:9px;overflow:hidden;background:var(--pc-cream);}',
    '.pc-cthumb img{width:100%;height:100%;object-fit:cover;display:block;}',
    '.pc-cinfo{flex:1;min-width:0;}',
    '.pc-cname{display:block;font-size:12.5px;font-weight:600;color:var(--pc-ink);text-decoration:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
    '.pc-cmeta{font-size:12px;color:var(--pc-magenta);font-weight:700;margin-top:2px;}',
    '.pc-cqty{color:var(--pc-muted);font-weight:600;}',
    '.pc-crm{flex:0 0 auto;width:26px;height:26px;border-radius:50%;border:1px solid var(--pc-line);background:#fff;color:var(--pc-muted);font-size:17px;line-height:1;cursor:pointer;transition:background .14s,color .14s,border-color .14s;}',
    '.pc-crm:hover{background:#fdecef;color:#b00020;border-color:#f3c6d2;}',
    '.pc-cfoot{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px 13px;border-top:1px solid var(--pc-line);}',
    '.pc-csub{font-size:13px;color:var(--pc-ink);}.pc-csub b{font-size:14.5px;color:var(--pc-magenta-d);margin-left:5px;}',
    '.pc-ccheckout{padding:10px 16px;border:0;border-radius:11px;cursor:pointer;font-weight:800;font-size:13px;color:#3a2400;',
    'background:linear-gradient(135deg,var(--pc-gold),var(--pc-gold-l));box-shadow:0 8px 18px -9px rgba(224,138,30,.65);transition:filter .14s,transform .12s;}',
    '.pc-ccheckout:hover{filter:brightness(1.05);transform:translateY(-1px);}',

    /* ---- Checkout ---- */
    '.pc-checkout:empty{display:none;}.pc-checkout{align-self:flex-start;width:100%;}',
    '.pc-cta{display:inline-flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:13px;border-radius:13px;',
    'background:linear-gradient(135deg,var(--pc-gold),var(--pc-gold-l));color:#3a2400;font-weight:800;font-size:14px;',
    'text-decoration:none;letter-spacing:.2px;box-shadow:0 10px 22px -10px rgba(224,138,30,.7);transition:filter .15s,transform .12s;}',
    '.pc-cta:hover{filter:brightness(1.05);transform:translateY(-1px);}',

    /* ---- Error ---- */
    '.pc-err{align-self:center;font-size:12.5px;color:#9a0026;background:#fde9ee;border:1px solid #f3c6d2;',
    'padding:9px 13px;border-radius:11px;max-width:92%;text-align:center;line-height:1.4;}',

    /* ---- Chips (welcome) ---- */
    '.pc-chips{display:flex;flex-wrap:wrap;gap:7px;align-self:flex-start;margin-top:1px;}',
    '.pc-chip{font-size:12px;font-weight:600;color:var(--pc-magenta);background:rgba(153,0,71,.07);border:1px solid var(--pc-line);',
    'padding:7px 12px;border-radius:999px;cursor:pointer;transition:background .15s,transform .12s,color .15s;}',
    '.pc-chip:hover{background:var(--pc-magenta);color:#fff;transform:translateY(-1px);}',

    /* ---- Composer ---- */
    '.pc-form{display:flex;align-items:flex-end;gap:9px;padding:11px 12px;background:var(--pc-cream);border-top:1px solid var(--pc-line);}',
    '.pc-ta{flex:1;resize:none;border:1px solid var(--pc-line);border-radius:15px;padding:11px 14px;font-family:var(--pc-body);',
    'font-size:14px;line-height:1.4;max-height:120px;color:var(--pc-ink);background:var(--pc-card);outline:none;transition:border-color .15s,box-shadow .15s;}',
    '.pc-ta:focus{border-color:var(--pc-magenta);box-shadow:0 0 0 3px rgba(153,0,71,.1);}',
    '.pc-ta::placeholder{color:var(--pc-muted);}',
    '.pc-ta:disabled{opacity:.6;}',
    '.pc-send{flex:0 0 auto;width:44px;height:44px;border:0;border-radius:14px;cursor:pointer;color:#fff;display:grid;place-items:center;',
    'background:linear-gradient(135deg,var(--pc-magenta),#bd0058);box-shadow:0 8px 18px -8px rgba(115,0,58,.6);transition:opacity .15s,transform .12s;}',
    '.pc-send:active{transform:scale(.92);}',
    '.pc-send:disabled{opacity:.38;cursor:not-allowed;box-shadow:none;}',
    '.pc-foot{font-size:10px;color:var(--pc-muted);text-align:center;padding:0 0 9px;background:var(--pc-cream);letter-spacing:.2px;}'
  ].join('');

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function safeUrl(u) {
    try {
      var p = new URL(u, location.href);
      return (p.protocol === 'https:' || p.protocol === 'http:') ? p.href : '#';
    } catch (e) { return '#'; }
  }

  // Inline markdown for a single line (escapes first, then a safe subset).
  function inlineMd(t) {
    var s = esc(t);
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, function (m, label, url) {
      return '<a href="' + esc(safeUrl(url)) + '" target="_blank" rel="noopener">' + label + '</a>';
    });
    s = s.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, function (m, pre, url) {
      return pre + '<a href="' + esc(safeUrl(url)) + '" target="_blank" rel="noopener">' + url + '</a>';
    });
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return s;
  }

  function splitRow(l) {
    return l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(function (c) { return c.trim(); });
  }

  // Line-based safe markdown: handles GFM tables, headings, bold, links.
  function md(text) {
    // Drop redundant "Image: https://..." lines the assistant echoes per product.
    var src = String(text || '').replace(/^[ \t]*image:\s*https?:\/\/\S+[ \t]*$/gim, '');
    var lines = src.split('\n');
    var isRow = function (l) { return /^\s*\|.*\|\s*$/.test(l); };
    var isSep = function (l) { return /-/.test(l) && /^\s*\|?[\s:|-]+\|?\s*$/.test(l); };
    var out = [];
    var i = 0;
    while (i < lines.length) {
      var line = lines[i];
      if (isRow(line) && i + 1 < lines.length && isSep(lines[i + 1])) {
        var head = splitRow(line);
        i += 2;
        var body = [];
        while (i < lines.length && isRow(lines[i])) { body.push(splitRow(lines[i])); i++; }
        var thead = '<tr>' + head.map(function (c) { return '<th>' + inlineMd(c) + '</th>'; }).join('') + '</tr>';
        var tbody = body.map(function (r) {
          return '<tr>' + r.map(function (c) { return '<td>' + inlineMd(c) + '</td>'; }).join('') + '</tr>';
        }).join('');
        out.push('<div class="pc-tablewrap"><table class="pc-table"><thead>' + thead + '</thead><tbody>' + tbody + '</tbody></table></div>');
        continue;
      }
      var h = line.match(/^\s{0,3}#{1,6}\s+(.*)$/);
      if (h) { out.push('<div class="pc-h">' + inlineMd(h[1]) + '</div>'); i++; continue; }
      out.push(inlineMd(line));
      i++;
    }
    // Block elements shouldn't get a <br> glued to them; join the rest with <br>.
    var html = '';
    for (var k = 0; k < out.length; k++) {
      var block = /^<(div|table)/.test(out[k]);
      if (k > 0 && !block && !/^<(div|table)/.test(out[k - 1])) html += '<br>';
      else if (k > 0) html += '';
      html += out[k];
    }
    return html.replace(/(<br>\s*){3,}/g, '<br><br>');
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  var CHIPS = ['Sambalpuri silk saree', 'Cotton saree under \u20b95,000', 'Red saree for a wedding', 'Track my order'];

  // Section header per product `group`; absent/"search"/"details" get no header.
  var GROUP_LABELS = { recommendation: 'You might also like', discount: 'Special offers' };

  class PriyaChat extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      this.backend = (this.getAttribute('backend') || DEFAULT_BACKEND).replace(/\/+$/, '');
      this.shop = (this.getAttribute('shop-url') || DEFAULT_SHOP).replace(/\/+$/, '');
      this.session = this._session();
      // Page context (set by Liquid only on product/collection pages) \u2192 lets the
      // agent answer "tell me about this". Empty handles are omitted.
      this.ctx = {};
      var ph = (this.getAttribute('product-handle') || '').trim();
      var ch = (this.getAttribute('collection-handle') || '').trim();
      if (ph) this.ctx.productHandle = ph;
      if (ch) this.ctx.collectionHandle = ch;
      this.streaming = false;
      this.greeted = false;
      this._build();
      this._wire();
    }

    _session() {
      try {
        var id = localStorage.getItem(SESSION_KEY);
        if (!id) {
          id = (crypto.randomUUID && crypto.randomUUID()) ||
            ('pc-' + Date.now() + '-' + Math.random().toString(16).slice(2));
          localStorage.setItem(SESSION_KEY, id);
        }
        return id;
      } catch (e) {
        return 'pc-' + Date.now() + '-' + Math.random().toString(16).slice(2);
      }
    }

    _build() {
      var root = this.attachShadow({ mode: 'open' });
      root.innerHTML =
        '<style>' + CSS + '</style>' +
        '<button class="pc-launch" type="button" aria-label="Chat with Priya AI">' +
          '<span class="pc-sparkle">' + SVG.chat + '</span><span class="pc-launch-label">Ask Priya</span></button>' +
        '<div class="pc-panel" role="dialog" aria-label="Priya AI shopping assistant">' +
          '<div class="pc-head">' +
            '<div class="pc-mark">' + SVG.spark + '</div>' +
            '<div class="pc-titles"><h2>Priya AI</h2><p><span class="pc-dot"></span>Handloom shopping assistant</p></div>' +
            '<button class="pc-x" type="button" aria-label="Close chat">' + SVG.close + '</button>' +
          '</div>' +
          '<div class="pc-log" aria-live="polite"></div>' +
          '<form class="pc-form">' +
            '<textarea class="pc-ta" rows="1" maxlength="' + MAX_LEN + '" ' +
              'placeholder="Ask for sarees, fabrics, order status\u2026" aria-label="Message"></textarea>' +
            '<button class="pc-send" type="submit" aria-label="Send" disabled>' + SVG.send + '</button>' +
          '</form>' +
          '<div class="pc-foot">Powered by Priya AI \u00b7 prices in \u20b9</div>' +
        '</div>';

      this.launch = root.querySelector('.pc-launch');
      this.panel = root.querySelector('.pc-panel');
      this.log = root.querySelector('.pc-log');
      this.form = root.querySelector('.pc-form');
      this.ta = root.querySelector('.pc-ta');
      this.sendBtn = root.querySelector('.pc-send');
      root.querySelector('.pc-x').addEventListener('click', () => this._open(false));
    }

    _wire() {
      this.launch.addEventListener('click', () => this._open(true));

      this.ta.addEventListener('input', () => {
        this.ta.style.height = 'auto';
        this.ta.style.height = Math.min(this.ta.scrollHeight, 120) + 'px';
        this.sendBtn.disabled = this.streaming || !this.ta.value.trim();
      });

      this.ta.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          if (!this.sendBtn.disabled) this._submit();
        }
      });

      this.form.addEventListener('submit', (e) => { e.preventDefault(); this._submit(); });

      // Delegated quick-actions — all are just messages to the agent (no API change).
      this.log.addEventListener('click', (e) => {
        if (this.streaming || !e.target.closest) return;
        var chip = e.target.closest('.pc-chip');
        if (chip) { this._send(chip.dataset.q); return; }
        var add = e.target.closest('.pc-add');
        if (add) { this._send('add ' + add.dataset.handle + ' to my cart', 'Add \u201c' + add.dataset.title + '\u201d to cart'); return; }
        var rm = e.target.closest('.pc-crm');
        if (rm) { this._send('remove ' + rm.dataset.handle + ' from my cart', 'Remove \u201c' + rm.dataset.title + '\u201d'); return; }
        var co = e.target.closest('.pc-ccheckout');
        if (co) { this._send('checkout', 'Checkout'); return; }
      });
    }

    _open(yes) {
      this.panel.classList.toggle('is-open', yes);
      this.launch.classList.toggle('is-open', yes);
      if (yes) {
        if (!this.greeted) {
          this.greeted = true;
          var turn = this._botTurn();
          turn.append('Namaste! \ud83d\ude4f I can help you find handloom sarees, fabrics and dupattas \u2014 or check an order. What are you looking for?');
          turn.chips(CHIPS);
        }
        setTimeout(() => this.ta.focus(), 260);
      }
    }

    // A bot turn groups carousel + prose + checkout for one response.
    _botTurn() {
      var msg = el('div', 'pc-msg bot');
      var typing = el('div', 'pc-typing', '<span></span><span></span><span></span>');
      var cart = el('div', 'pc-cartwrap');
      var sections = el('div', 'pc-sections');
      var notice = el('div', 'pc-notice');
      var bubble = el('div', 'pc-bubble');
      var chips = el('div', 'pc-chips');
      var checkout = el('div', 'pc-checkout');
      msg.append(typing, cart, sections, notice, bubble, chips, checkout);
      this.log.append(msg);
      this._scroll();
      var self = this;
      var text = '';
      var groups = {};   // product `group` -> its own carousel section

      function sectionFor(key) {
        if (groups[key]) return groups[key];
        var wrap = el('div', 'pc-section');
        var label = GROUP_LABELS[key];
        if (label) wrap.append(el('div', 'pc-section-head', esc(label)));
        var car = self._carousel();
        wrap.append(car.root);
        sections.append(wrap);
        groups[key] = car;
        return car;
      }

      return {
        addProduct: function (p) { typing.remove(); sectionFor(p.group || 'search').add(self._card(p)); self._scroll(); },
        addCart: function (data) { typing.remove(); cart.innerHTML = self._cartPanel(data); self._scroll(); },
        addText: function (delta) { typing.remove(); text += delta; bubble.innerHTML = md(text); self._scroll(); },
        append: function (str) { typing.remove(); text += str; bubble.innerHTML = md(text); self._scroll(); },
        chips: function (list) {
          chips.innerHTML = list.map(function (q) { return '<button class="pc-chip" data-q="' + esc(q) + '">' + esc(q) + '</button>'; }).join('');
          self._scroll();
        },
        addCheckout: function (url) {
          checkout.innerHTML = '<a class="pc-cta" href="' + esc(safeUrl(url)) + '" target="_blank" rel="noopener">Proceed to checkout \u2192</a>';
          self._scroll();
        },
        notice: function (msg) { notice.textContent = msg; self._scroll(); },
        clearNotice: function () { notice.textContent = ''; },
        finish: function () { typing.remove(); notice.textContent = ''; }
      };
    }

    // Build a horizontal scroll-snap carousel with edge fades + nav arrows.
    _carousel() {
      var root = el('div', 'pc-carousel');
      var track = el('div', 'pc-track');
      var fadeL = el('div', 'pc-fade l');
      var fadeR = el('div', 'pc-fade r');
      var prev = el('button', 'pc-nav prev', SVG.arrow);
      var next = el('button', 'pc-nav next', SVG.arrow);
      prev.type = next.type = 'button';
      prev.setAttribute('aria-label', 'Previous'); next.setAttribute('aria-label', 'Next');
      root.append(fadeL, fadeR, prev, next, track);

      function sync() {
        var max = track.scrollWidth - track.clientWidth - 1;
        root.dataset.l = track.scrollLeft > 4 ? '1' : '0';
        root.dataset.r = (max > 4 && track.scrollLeft < max) ? '1' : '0';
      }
      track.addEventListener('scroll', sync, { passive: true });
      prev.addEventListener('click', function () { track.scrollBy({ left: -track.clientWidth * 0.8, behavior: 'smooth' }); });
      next.addEventListener('click', function () { track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' }); });

      return {
        root: root,
        add: function (html) { track.insertAdjacentHTML('beforeend', html); requestAnimationFrame(sync); }
      };
    }

    _card(p) {
      var oos = p.in_stock === false;
      var url = safeUrl(p.url || (this.shop + '/products/' + encodeURIComponent(p.handle || '')));
      var img = p.image || PLACEHOLDER;
      var priceNow = p.price || '';
      var priceWas = p.compare_at_price || '';
      var tag = oos ? '<span class="pc-tag oos">Sold out</span>'
        : (priceWas ? '<span class="pc-tag sale">Sale</span>' : '');
      var was = priceWas ? '<span class="pc-was">' + esc(priceWas) + '</span>' : '';
      // "Add to cart" is a quick-action \u2192 sends a message (see _wire). Hidden when sold out.
      var add = oos ? '' : '<button class="pc-add" type="button" data-handle="' + esc(p.handle || '') +
        '" data-title="' + esc(p.title) + '">Add to cart</button>';
      return '<div class="pc-card' + (oos ? ' oos' : '') + '">' +
        '<a class="pc-card-link" href="' + esc(url) + '" target="_blank" rel="noopener">' +
          '<div class="pc-imgwrap"><img src="' + esc(img) + '" alt="" loading="lazy" ' +
          'onerror="this.onerror=null;this.src=\'' + PLACEHOLDER + '\'">' + tag + '</div>' +
          '<div class="pc-cbody"><div class="pc-ctitle">' + esc(p.title) + '</div>' +
          '<div class="pc-prices"><span class="pc-price">' + esc(priceNow) + '</span>' + was + '</div></div>' +
        '</a>' + add + '</div>';
    }

    // Canonical cart panel (event:cart). Empty/cleared \u2192 {itemCount:0,lines:[]}.
    _cartPanel(data) {
      if (!data || !data.itemCount || !(data.lines && data.lines.length)) {
        return '<div class="pc-cart pc-cart--empty">Your cart is empty.</div>';
      }
      var self = this;
      var lines = data.lines.map(function (l) {
        var url = safeUrl(l.url || (self.shop + '/products/' + encodeURIComponent(l.handle || '')));
        var img = l.image || PLACEHOLDER;
        var qty = l.quantity != null ? l.quantity : 1;
        return '<div class="pc-cline">' +
          '<a class="pc-cthumb" href="' + esc(url) + '" target="_blank" rel="noopener">' +
            '<img src="' + esc(img) + '" alt="" loading="lazy" onerror="this.onerror=null;this.src=\'' + PLACEHOLDER + '\'"></a>' +
          '<div class="pc-cinfo"><a class="pc-cname" href="' + esc(url) + '" target="_blank" rel="noopener">' + esc(l.title) + '</a>' +
          '<div class="pc-cmeta">' + esc(l.price || '') + ' <span class="pc-cqty">\u00d7 ' + esc(qty) + '</span></div></div>' +
          '<button class="pc-crm" type="button" data-handle="' + esc(l.handle || '') + '" data-title="' + esc(l.title) +
          '" aria-label="Remove ' + esc(l.title) + '">\u00d7</button>' +
        '</div>';
      }).join('');
      var n = data.itemCount;
      return '<div class="pc-cart">' +
        '<div class="pc-chead"><span class="pc-ctitle2">Your cart</span>' +
        '<span class="pc-ccount">' + n + ' item' + (n !== 1 ? 's' : '') + '</span></div>' +
        '<div class="pc-clines">' + lines + '</div>' +
        '<div class="pc-cfoot"><span class="pc-csub">Subtotal <b>' + esc(data.subtotal || '') + '</b></span>' +
        '<button class="pc-ccheckout" type="button">Checkout \u2192</button></div></div>';
    }

    _userMsg(text) {
      var msg = el('div', 'pc-msg user');
      var bubble = el('div', 'pc-bubble');
      bubble.textContent = text;
      msg.append(bubble);
      this.log.append(msg);
      this._scroll();
    }

    _error(turn, message) {
      if (turn) turn.finish();
      var box = el('div', 'pc-err');
      box.textContent = message || 'Something went wrong. Please try again.';
      this.log.append(box);
      this._scroll();
    }

    _scroll() { this.log.scrollTop = this.log.scrollHeight; }

    _setStreaming(on) {
      this.streaming = on;
      this.sendBtn.disabled = on || !this.ta.value.trim();
      this.ta.disabled = on;
    }

    _submit() {
      if (this.streaming) return;
      var text = this.ta.value.trim();
      if (!text) return;
      this.ta.value = '';
      this.ta.style.height = 'auto';
      this._send(text);
    }

    // Send a message. displayText (optional) is the bubble shown to the user
    // when it should differ from the raw text sent (e.g. "Add to cart").
    async _send(text, displayText) {
      if (this.streaming || !text) return;
      if (text.length > MAX_LEN) { this._error(null, 'That message is too long — please shorten it.'); return; }
      this._userMsg(displayText || text);
      this._setStreaming(true);

      var turn = this._botTurn();
      try {
        await this._stream(text, turn);
      } catch (e) {
        if (!e || e.name !== 'AbortError') this._error(turn, 'Network hiccup — please try again.');
      } finally {
        turn.finish();
        this._setStreaming(false);
        this.ta.focus();
      }
    }

    async _stream(text, turn, attempt) {
      attempt = attempt || 0;
      var body = { text: text };
      if (this.ctx && (this.ctx.productHandle || this.ctx.collectionHandle)) body.context = this.ctx;

      var res = await fetch(this.backend + '/agents/shopping-assistant/' + encodeURIComponent(this.session), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body)
      });

      // Rate limited \u2192 wait for Retry-After (capped) and retry a few times.
      if (res.status === 429 && attempt < 3) {
        var ra = parseFloat(res.headers.get('Retry-After'));            // seconds; null if not CORS-exposed
        var waitMs = Math.min(isFinite(ra) && ra > 0 ? ra * 1000 : 2000, 15000);
        turn.notice('One moment\u2026');
        await new Promise(function (r) { setTimeout(r, waitMs); });
        turn.clearNotice();
        return this._stream(text, turn, attempt + 1);
      }

      if (!res.ok) { this._error(turn, this._httpMessage(res.status)); return; }
      if (!res.body) { this._error(turn, 'Streaming is not supported in this browser.'); return; }
      turn.clearNotice();

      var reader = res.body.getReader();
      var decoder = new TextDecoder();
      var buf = '';
      while (true) {
        var chunk = await reader.read();
        if (chunk.done) break;
        buf += decoder.decode(chunk.value, { stream: true });
        var sep;
        while ((sep = buf.indexOf('\n\n')) !== -1) {
          var frame = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          this._frame(frame, turn);
        }
      }
      buf += decoder.decode();
      if (buf.trim()) this._frame(buf, turn);
    }

    _frame(frame, turn) {
      var event = 'message';
      var data = '';
      var lines = frame.split('\n');
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (line.indexOf('event:') === 0) event = line.slice(6).trim();
        else if (line.indexOf('data:') === 0) data += line.slice(5).trim();
      }
      var payload = {};
      if (data) { try { payload = JSON.parse(data); } catch (e) { return; } }

      switch (event) {
        case 'text': if (payload.delta) turn.addText(payload.delta); break;
        case 'product': turn.addProduct(payload); break;
        case 'cart': turn.addCart(payload); break;
        case 'checkout': if (payload.url && !payload.skipped) turn.addCheckout(payload.url); break;
        case 'error': this._error(turn, payload.message); break;
        case 'done': turn.finish(); break;
      }
    }

    _httpMessage(status) {
      if (status === 400) return 'Please type a message first.';
      if (status === 403) return 'The assistant isn\u2019t available on this site.';
      if (status === 413) return 'That message is too long — please shorten it.';
      if (status === 429) return 'A lot of shoppers right now — please try again in a moment.';
      return 'The assistant is unavailable right now. Please try again shortly.';
    }
  }

  customElements.define('priya-chat', PriyaChat);
})();
